import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { RippleModule } from "primeng/ripple";
import { PrimeNgSeverity } from "../../../../types/primeng";

/* 
  Example usage (card style with icon and severity options):
  {
    type: "note",
    props: {
      content: {
        en: "Some good note between fields",
        fi: "Hyvä huomautus kenttien välissä",
        sv: "Bra anteckning mellan fälten",
      },
      severity: "info",                       // OPTIONAL: Can be "secondary" | "info" | "success" | "warn" | "danger" | undefined (default: 'info') NOTE: Works only with cardStyle: true
      showIcon: true,                         // OPTIONAL: Whether to show an icon in the note (default: false)
      cardStyle: true,                        // OPTIONAL: Whether to apply card style to the note (default: false)
      icon: "pi-info-circle",                 // OPTIONAL: Icon class from PrimeNG icons
      marginTop: 1,                           // OPTIONAL: Number of rems for top margin (default: 0)
      marginBottom: 1,                        // OPTIONAL: Number of rems for bottom margin (default: 0)
    },
  },


  // The simplest definition (without card styling):
  {
    type: "note",
    props: {
      content: {
        en: "Some good note between fields",
        fi: "Hyvä huomautus kenttien välissä",
        sv: "Bra anteckning mellan fälten",
      },
    },
  },



  ////////////////////

  JSON example:

  {
    "type": "note",
    "props": {
      "content": {
        "en": "Some good note between fields",
        "fi": "Hyvä huomautus kenttien välissä",
        "sv": "Bra anteckning mellan fälten"
      },
      "severity": "info",                       // OPTIONAL: Can be "secondary" | "info" | "success" | "warn" | "danger" | undefined (default: 'info') NOTE: Works only with cardStyle: true
      "showIcon": true,                         // OPTIONAL: Whether to show an icon in the note (default: false)
      "cardStyle": true,                        // OPTIONAL: Whether to apply card style to the note (default: false)
      "icon": "pi-info-circle",                 // OPTIONAL: Icon class from PrimeNG icons
      "marginTop": 1,                           // OPTIONAL: Number of rems for top margin (default: 0)
      "marginBottom": 1                        // OPTIONAL: Number of rems for bottom margin (default: 0)
    }
  },

*/

@Component({
  selector: "app-formly-type-note",
  templateUrl: "./note.type.html",
  standalone: true,
  imports: [CommonModule, RippleModule],
})
export class NoteComponent extends FieldType<FieldTypeConfig> {
  visible = true;

  get content(): string {
    return this.props["content"] ?? "";
  }

  get severity(): PrimeNgSeverity {
    return (this.props["severity"] as PrimeNgSeverity) ?? "info";
  }

  get icon(): string {
    return this.props["icon"] ?? this.getDefaultIcon();
  }

  get marginTop(): string {
    return String(this.props["marginTop"] ?? 0);
  }

  get marginBottom(): string {
    return String(this.props["marginBottom"] ?? 0);
  }

  get severityClasses(): Record<string, string> {
    return {
      info: "bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300",
      success:
        "bg-green-50 border-green-200 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300",
      warn: "bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-300",
      danger: "bg-red-50 border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300",
    };
  }

  get noteClass(): string {
    return `rounded-md border ${this.severityClasses[this.severity || "info"]} p-4`;
  }

  private getDefaultIcon(): string {
    const icons = {
      secondary: "pi pi-info-circle",
      info: "pi pi-info-circle",
      success: "pi pi-check-circle",
      warn: "pi pi-exclamation-triangle",
      danger: "pi pi-times-circle",
    };
    return icons[this.severity || "info"] || icons.info;
  }
}
